import React, { useEffect, useMemo, useRef, useState } from 'react'
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';

import { useForm } from 'react-hook-form';
import creditCardType from "credit-card-type";
import getCardsTypeWise from '../../assets/cards';
import "./cardForm.css"
import { InputAdornment, InputBase, TextField, withStyles } from '@material-ui/core';
import { useAcceptJs } from 'react-acceptjs';

import { getApiLoginIdAndClientKey } from "../../../_metronic/_helpers";


export const MyTextField = withStyles({
  root: {
    fontFamily: "Inter",
    '& .MuiInput-underline:after': {
      borderBottomColor: '#717171',
    },
    '& .MuiFormLabel-root': {
      fontSize: `12px`,
      fontWeight: 400,
      lineHeight: `16px`,

    },
    '& .MuiInputBase-root': {
      backgroundColor: '#f7f7f7 !important',
      borderRadius: "8px !important",
    },
    '& .MuiFilledInput-input': {
      padding: '24px 16px 10px',
      color: '#717171',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
    },
    '& .MuiInputLabel-filled': {
      transform: 'translate(16px, 20px) scale(1)',
    },
    '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
      transform: 'translate(16px, 10px) scale(0.75)',
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#717171',
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: '#b41f1f',
    },
    
  },
})(TextField);

const isValidDate = (date="") => {
  if (date.length === 5) {
    const [enteredMonth, enteredYear] = date.split('/').map(num => parseInt(num, 10));
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = parseInt(currentDate.getFullYear().toString().slice(-2), 10); 
  
    if (enteredYear < currentYear || (enteredYear === currentYear && enteredMonth < currentMonth)) {
      return false;
    } else {
      return true;
    }
  }
  return !date;
}

const AddCardForm = ({ variant = 'dev', isThirdParty = false }) => {
  const { apiLoginId, clientKey } = useMemo(() => getApiLoginIdAndClientKey(), []);
  const authData = {
    apiLoginID: apiLoginId,
    clientKey
  };

  // Determine environment based on variant prop
  const environment = variant === 'prod' ? 'PRODUCTION' : 'SANDBOX';

  const { dispatchData, loading } = useAcceptJs({ environment, authData });

  const { handleSubmit, setValue, watch, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      cardNumber: '',
      expiryDate: '',
      cardCode: '',
      firstName: '',
      lastName: '',
      zipCode: '',
    }
  });

  const [cardType, setCardType] = useState(null);
  const [isAcceptJsReady, setIsAcceptJsReady] = useState(false);
  const [acceptJsError, setAcceptJsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const watchCardNumber = watch('cardNumber');
  const watchExpiryDate = watch('expiryDate');
  const watchCardCode = watch('cardCode');
  const watchFirstName = watch('firstName');
  const watchLastName = watch('lastName');
  const watchZipCode = watch('zipCode');

  const expiryDateRef = useRef(null);
  const cvvRef = useRef(null);
  const nameRef = useRef(null);

  const isValidExpDate = isValidDate(watchExpiryDate);

  // Card number length validation
  const cardNumberLength = watchCardNumber ? watchCardNumber.length : 0;
  const expectedCardLength = (cardType && cardType.lengths && cardType.lengths[0]) || 16;
  const isCardNumberValid = cardNumberLength >= expectedCardLength;

  const baseValidation = !!(isValidExpDate && watchCardCode && watchExpiryDate && isCardNumberValid);

  const isValid = isThirdParty
    ? !!(baseValidation && watchFirstName && watchLastName && watchZipCode)
    : !!baseValidation;

  useEffect(() => {
    if (!watchCardNumber) {
      setCardType(null);
    }
  }, [watchCardNumber])

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 50;

    const checkAcceptJs = setInterval(() => {
      attempts++;

      if (window.Accept) {
        clearInterval(checkAcceptJs);
        setIsAcceptJsReady(true);
        setAcceptJsError(false);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkAcceptJs);
        setIsAcceptJsReady(false);
        setAcceptJsError(true);

        const errorDetails = {
          error: 'ACCEPT_JS_LOAD_TIMEOUT',
        };

        // Send to React Native WebView
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify(errorDetails));
        }

        // For Chrome Custom Tabs - redirect to deep link with error
        const deepLinkUrl = `telemedicine://app/payment-error?error=${encodeURIComponent('ACCEPT_JS_LOAD_TIMEOUT')}`;
        window.location.href = deepLinkUrl;
      }
    }, 100);

    return () => clearInterval(checkAcceptJs);
  }, []);

  const handleCardNumberChange = (e) => {
    const inputValue = e.target.value.replace(/\D/g, ''); // Remove non-digit characters
    const cardValidation = creditCardType(inputValue)?.[0];
    if (cardValidation) {
      setCardType(cardValidation); // Set detected card type
    } else {
      setCardType(null);
    }

    const formattedCardNumber = inputValue.replace(/(.{4})/g, '$1 ').trim();
    setValue('cardNumber', formattedCardNumber);

    // Auto-focus next field when card number is complete
    const expectedLength = (cardValidation && cardValidation.lengths && cardValidation.lengths[0]) || 16;
    if (inputValue.length >= expectedLength) {
      setTimeout(function() {
        if (expiryDateRef.current) {
          const input = expiryDateRef.current.querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }, 0);
    }
  };

  // Handle expiry date input
  const handleExpiryDateChange = (e) => {
    // Get only digits from input
    let input = e.target.value.replace(/\D/g, '');

    // Limit to 4 digits max (MMYY)
    input = input.substring(0, 4);

    // Auto-format month with leading zero
    if (input.length === 1 && parseInt(input, 10) > 1) {
      input = `0${input}`;
    }

    // Validate and fix month if > 12
    if (input.length >= 2) {
      const month = parseInt(input.substring(0, 2), 10);
      if (month > 12) {
        input = '12' + input.substring(2);
      } else if (month === 0) {
        input = '01' + input.substring(2);
      }
    }

    // Add slash after month (MM/YY format)
    let formattedInput = input;
    if (input.length > 2) {
      formattedInput = `${input.substring(0, 2)}/${input.substring(2, 4)}`;
    }

    // Update the value
    setValue('expiryDate', formattedInput);

    // Validate and auto-focus when complete (MM/YY = 5 characters)
    if (formattedInput.length === 5) {
      const [enteredMonth, enteredYear] = formattedInput.split('/').map(function(num) { return parseInt(num, 10); });
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = parseInt(currentDate.getFullYear().toString().slice(-2), 10);

      if (enteredYear < currentYear || (enteredYear === currentYear && enteredMonth < currentMonth)) {
        setError('expiryDate', {
          message: 'Expiry date cannot be in the past',
        }, { shouldFocus: true });
      } else {
        clearErrors('expiryDate');
        // Auto-focus to CVV field
        setTimeout(function() {
          if (cvvRef.current) {
            const cvvInput = cvvRef.current.querySelector('input');
            if (cvvInput) {
              cvvInput.focus();
            }
          }
        }, 0);
      }
    }
  };

  const handleCardCodeChange = (e) => {
    let input = e.target.value.replace(/\D\s/g, ''); // Remove non-digit and space characters
    setValue('cardCode', input);

    // Auto-focus to name field when CVV is complete (only for third-party)
    const expectedCvvLength = (cardType && cardType.code && cardType.code.size) || 3;
    if (isThirdParty && input.length === expectedCvvLength) {
      setTimeout(function() {
        if (nameRef.current) {
          const nameInput = nameRef.current.querySelector('input');
          if (nameInput) {
            nameInput.focus();
          }
        }
      }, 0);
    }
  };

  const sendErrorToReactNative = function(errorData) {
    let errorCode = 'UNKNOWN_ERROR';

    if (errorData && errorData.messages && errorData.messages.message && errorData.messages.message[0]) {
      errorCode = errorData.messages.message[0].code;
    }

    const errorDetails = {
      error: errorCode,
    };

    // Send to React Native WebView
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(errorDetails));
    } else {
      // For Chrome Custom Tabs - Direct redirect to error deep link
      const deepLinkUrl = `telemedicine://app/payment-error?error=${encodeURIComponent(errorCode)}`;
      window.location.href = deepLinkUrl;
    }
  };

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const sanitizedCardNumber = formData.cardNumber.replace(/\s/g, '');

      const payload = {
        cardCode: formData.cardCode.replace(/\s/g, ''),
        month: formData.expiryDate.substring(0, 2),
        year: formData.expiryDate.substring(3, 5),
        cardNumber: sanitizedCardNumber,
      };

      const data = await dispatchData({ cardData: payload });

      if (data && data.messages && data.messages.resultCode === "Ok") {
        const cardDetails = {
          cardNumber: sanitizedCardNumber.slice(-4),
          expDate: formData.expiryDate,
          token: data.opaqueData.dataValue,
        };

        if (isThirdParty) {
          cardDetails.firstName = formData.firstName;
          cardDetails.lastName = formData.lastName;
          cardDetails.zipCode = formData.zipCode;
        }

        // Send to React Native WebView
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify(cardDetails));
        } else {
          // For Chrome Custom Tabs - Direct redirect to deep link
          const deepLinkUrl = `telemedicine://app/payment-success?data=${encodeURIComponent(JSON.stringify(cardDetails))}`;
          window.location.href = deepLinkUrl;
        }
      } else {
        setIsSubmitting(false);
        sendErrorToReactNative(data);
      }
    } catch (err) {
      setIsSubmitting(false);
      sendErrorToReactNative(err);
    }
  };

  // Show loading message while Accept.js loads
  if (!isAcceptJsReady && !acceptJsError) {
    return (
      <div className='card-form-wrapper'>
        <div className="card-form-container">
          <div style={{
            padding: '24px',
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            <p style={{ margin: 0, color: '#856404' }}>Loading payment processor...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if Accept.js failed to load
  if (acceptJsError) {
    return (
      <div className='card-form-wrapper'>
        <div className="card-form-container">
          <div style={{
            padding: '24px',
            textAlign: 'center',
            backgroundColor: '#f8d7da',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            <p style={{ margin: 0, color: '#721c24', marginBottom: '12px' }}>
              Payment processor failed to load. Please check your connection and try again.
            </p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='card-form-wrapper'>
      <div className="card-form-container">
        {/* Loading Overlay */}
        {isSubmitting && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f3f3',
              borderTop: '5px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
            <p style={{
              color: '#fff',
              marginTop: '20px',
              fontSize: '16px',
              fontWeight: '500',
            }}>
              Processing your card...
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <h1 className="form-title">Card Information</h1>

          {/* Card Number Input */}
          <div className="input-container">
            <div className="card-number-field" style={{ display: 'flex', alignItems: 'center' }}>
              <InputBase
                name="cardNumber"
                value={watchCardNumber || ''}
                label="Card Number"
                placeholder="0000 0000 0000 0000"
                variant="filled"
                className='base-input-card'
                fullWidth
                onChange={handleCardNumberChange}
                inputProps={{ maxLength: 19 }}
                helperText={errors.cardNumber && 'Card number is required'}
                disabled={!isAcceptJsReady}
                startAdornment= {
                  <InputAdornment position="start">
                    {getCardsTypeWise(cardType?.type)}
                  </InputAdornment>
                }
              />
              
            </div>
          </div>

          <div className='input-container-group'>
            {/* Expiry Date Input */}
          <div className="input-container">
            <MyTextField
              name="expiryDate"
              value={watchExpiryDate || ''}
              label="Expiry Date (MM/YY)"
              variant="filled"
              fullWidth
              error={!isValidExpDate}
              inputProps={{ maxLength: 5 }}
              onChange={handleExpiryDateChange}
              disabled={!isAcceptJsReady}
              InputProps={{
                disableUnderline: true,
              }}
              ref={expiryDateRef}
            />
          </div>

          {/* CVV Input */}
          <div className="input-container">
            <MyTextField
              name="cardCode"
              value={watchCardCode || ''}
              label={cardType ? cardType.code.name : "CVV"}
              type="password"
              variant="filled"
              onChange={handleCardCodeChange}
              fullWidth
              inputProps={{ maxLength: cardType ? cardType?.code?.size : 3 }}
              disabled={!isAcceptJsReady}
              InputProps={{
                disableUnderline: true,
              }}
              ref={cvvRef}
            />
          </div>

          </div>
          {/* Third Party Customer Info Fields */}
          {isThirdParty && (
            <>
              <div className='input-container-group'>
                <div className="input-container">
                  <MyTextField
                    label="First Name"
                    variant="filled"
                    fullWidth
                    value={watchFirstName || ''}
                    onChange={(e) => setValue('firstName', e.target.value, { shouldValidate: true })}
                    disabled={!isAcceptJsReady}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    ref={nameRef}
                  />
                </div>
                <div className="input-container">
                  <MyTextField
                    label="Last Name"
                    variant="filled"
                    fullWidth
                    value={watchLastName || ''}
                    onChange={(e) => setValue('lastName', e.target.value, { shouldValidate: true })}
                    disabled={!isAcceptJsReady}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  />
                </div>
              </div>
              <div className="input-container">
                <MyTextField
                  label="Zip Code"
                  variant="filled"
                  fullWidth
                  value={watchZipCode || ''}
                  onChange={(e) => setValue('zipCode', e.target.value, { shouldValidate: true })}
                  inputProps={{ maxLength: 10 }}
                  disabled={!isAcceptJsReady}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </div>
            </>
          )}

          {/* Submit Button */}
          <PrimaryButton
            type="submit"
            disabled={loading || isSubmitting || !isValid || !isAcceptJsReady}
            fullWidth={true}
          >
            {loading || isSubmitting ? 'Processing...' : 'Save and add new card'}
          </PrimaryButton>
        </form>
      </div>
    </div>
  )
}

export default AddCardForm;
